import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, PrimaryButton, DefaultButton } from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';

import {
  dismissGlobalMessage,
  fileErrorsSelector,
  globalMessageModes,
  globalMessageModeSelector,
  invalidKleNumberSelector,
  fieldValidationErrorsSelector,
} from '../features/globalMessageSlice';
import {
  currentUserDisplayNameSelector,
  loginFlowInitialize,
} from '../features/authenticationSlice';
import {
  confirmNotificationOfHearingUpdates,
  declineNotificationOfHearingUpdates,
} from '../features/componentFeatures/renderHearingSlice';
import { push } from 'connected-react-router';
import { MY_HEARINGS_WROTE_URL } from '../constants/urls';
import { setPageStep } from '../features/pageFeatures/createHearingPageSlice';
import { initializeNotificationPanelState } from '../features/componentFeatures/notificationPanelSlice';

const GlobalMessage = () => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();
  const globalMessageMode = useSelector(globalMessageModeSelector);
  const fileErrors = useSelector(fileErrorsSelector);
  const fieldValidationErrors = useSelector(fieldValidationErrorsSelector);
  const invalidKleNumbers = useSelector(invalidKleNumberSelector);
  const currentUserName = useSelector(currentUserDisplayNameSelector);
  const showModal = !!globalMessageMode;

  const dismiss = () => {
    dispatch(dismissGlobalMessage());
  };

  const dismissAndGoToInvitationStep = () => {
    dispatch(dismissGlobalMessage());
    dispatch(setPageStep(3));
  };

  const dismissAndNavigate = (path) => {
    dispatch(dismissGlobalMessage());
    dispatch(push(path));
  };

  const reLogin = () => {
    dispatch(loginFlowInitialize());
  };

  const localConfirmNotificationOfHearingUpdates = () => {
    dispatch(dismissGlobalMessage());
    dispatch(confirmNotificationOfHearingUpdates());
  };

  const localDeclineNotificationOfHearingUpdates = () => {
    dispatch(dismissGlobalMessage());
    dispatch(declineNotificationOfHearingUpdates());
  };

  const localConfirmCancelEditNotification = () => {
    dispatch(dismissGlobalMessage());
    dispatch(initializeNotificationPanelState());
  };

  const localDeclineCancelEditNotification = () => {
    dispatch(dismissGlobalMessage());
  };

  const modalContentErrorMessage = (errorMessage, closeMessage) => {
    return (
      <div className="modal-outer">
        <div className="modal-outer-content">
          <p>{errorMessage}</p>
        </div>
        <div className="global-message-modal-button-bar">
          <PrimaryButton text={closeMessage} onClick={dismiss} />
        </div>
      </div>
    );
  };

  const renderModalContent = (globalMessageMode) => {
    switch (globalMessageMode) {
      case globalMessageModes.loginExpired:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <p>
                {translator('components.globalMessage.loginExpiredDescription')}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator(
                  'components.globalMessage.loginExpiredReLogin',
                )}
                onClick={reLogin}
              />
              <DefaultButton
                text={translator('global.close')}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.generalError:
        return modalContentErrorMessage(
          translator('components.globalMessage.generalError'),
          translator('global.close'),
        );
      case globalMessageModes.emptyConclusionError:
        return modalContentErrorMessage(
          translator('components.globalMessage.emptyConclusionError'),
          translator('global.close'),
        );
      case globalMessageModes.sbsipAuthorizationError:
        return modalContentErrorMessage(
          translator('components.globalMessage.sbsipAuthorizationError', {
            currentUserName: currentUserName,
          }),
          translator('global.close'),
        );
      case globalMessageModes.unknownError:
        return modalContentErrorMessage(
          translator('components.globalMessage.unknownError'),
          translator('global.close'),
        );
      case globalMessageModes.notFoundError:
        return modalContentErrorMessage(
          translator('components.globalMessage.notFoundError'),
          translator('global.close'),
        );
      case globalMessageModes.forbiddenAccessError:
        return modalContentErrorMessage(
          translator('components.globalMessage.forbiddenAccessError'),
          translator('global.close'),
        );
      case globalMessageModes.invalidOperationError:
        return modalContentErrorMessage(
          translator('components.globalMessage.invalidOperationError'),
          translator('global.close'),
        );
      case globalMessageModes.unauthorizedAccessError:
        return modalContentErrorMessage(
          translator('components.globalMessage.unauthorizedAccessError'),
          translator('global.close'),
        );
      case globalMessageModes.validationError:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content__with-header">
              <p>{translator('components.globalMessage.validationError')}</p>
              <ul>
                {fieldValidationErrors.map((errorMsg, index) => (
                  <li key={index}>{`${errorMsg}`}</li>
                ))}
              </ul>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.close')}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.invalidModelStateError:
        return modalContentErrorMessage(
          translator('components.globalMessage.invalidModelStateError'),
          translator('global.close'),
        );
      case globalMessageModes.invalidCsvContentError:
        return modalContentErrorMessage(
          translator('components.globalMessage.invalidCsvContentError'),
          translator('global.close'),
        );
      case globalMessageModes.hearingPublished:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator('global.congratulations')}
              </h2>
              <p>
                {translator('components.globalMessage.hearingPublishedText')}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <DefaultButton
                text={translator(
                  'components.globalMessage.hearingPublishedGoToInvitationPageButton',
                )}
                onClick={dismissAndGoToInvitationStep}
              />

              <PrimaryButton
                text={translator(
                  'components.globalMessage.hearingPublishedConfirmButton',
                )}
                onClick={() => dismissAndNavigate(MY_HEARINGS_WROTE_URL)}
              />
            </div>
          </div>
        );
      case globalMessageModes.hearingSaved:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator('global.congratulations')}
              </h2>
              <p>{translator('components.globalMessage.hearingSavedText')}</p>
            </div>
            <div className="global-message-modal-button-bar">
              <DefaultButton
                text={translator(
                  'components.globalMessage.hearingSavedTextGoToInvitationPageButton',
                )}
                onClick={dismissAndGoToInvitationStep}
              />

              <PrimaryButton
                text={translator(
                  'components.globalMessage.hearingSavedTextConfirmButton',
                )}
                onClick={() => dismissAndNavigate(MY_HEARINGS_WROTE_URL)}
              />
            </div>
          </div>
        );
      case globalMessageModes.conclusionSaved:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator('global.congratulations')}
              </h2>
              <p>
                {translator('components.globalMessage.conclusionSavedText')}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <DefaultButton
                text={translator(
                  'components.globalMessage.conclusionSavedSecondaryButton',
                )}
                onClick={() => dismissAndNavigate(MY_HEARINGS_WROTE_URL)}
              />

              <PrimaryButton
                text={translator(
                  'components.globalMessage.conclusionSavedPrimaryButton',
                )}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.conclusionFinalized:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator('global.congratulations')}
              </h2>
              <p>
                {translator('components.globalMessage.conclusionFinalizedText')}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <DefaultButton
                text={translator(
                  'components.globalMessage.conclusionFinalizedSecondaryButton',
                )}
                onClick={() => dismissAndNavigate(MY_HEARINGS_WROTE_URL)}
              />

              <PrimaryButton
                text={translator(
                  'components.globalMessage.conclusionFinalizedPrimaryButton',
                )}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.commentReceived:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator('components.globalMessage.commentReceivedHeader')}
              </h2>
              <p>
                {translator('components.globalMessage.commentReceivedText')}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator(
                  'components.globalMessage.commentReceivedButton',
                )}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.changeActiveHearingText:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <p>{translator('components.globalMessage.notifyInvitees')}</p>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.yes')}
                onClick={localConfirmNotificationOfHearingUpdates}
              />
              <DefaultButton
                text={translator('global.no')}
                onClick={localDeclineNotificationOfHearingUpdates}
              />
            </div>
          </div>
        );
      case globalMessageModes.fileUploadError:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content__with-header">
              <p>{translator('components.globalMessage.fileUploadError')}</p>
              {fileErrors.length ? (
                fileErrors.map((fileError, index) => (
                  <p key={index}>{`${index + 1}. ${fileError}`}</p>
                ))
              ) : (
                <p>
                  {translator(
                    'components.globalMessage.fileUploadErrorNoFiles',
                  )}
                </p>
              )}
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.close')}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.kleValidationError:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content__with-header">
              {invalidKleNumbers.length ? (
                <div>
                  <p>
                    {translator(
                      'components.globalMessage.kleNumberValidateError',
                    )}
                  </p>
                  <ul>
                    {invalidKleNumbers.map((invalidKleNumber, index) => (
                      <li key={index}>{`${invalidKleNumber}`}</li>
                    ))}
                  </ul>
                  <p>
                    {translator(
                      'components.globalMessage.kleNumberValidateAdvicement',
                    )}
                  </p>
                </div>
              ) : (
                <p>
                  {translator(
                    'components.globalMessage.kleNumberValidateNoNumbers',
                  )}
                </p>
              )}
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.close')}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.cancelEditNotification:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <p>
                {translator('components.globalMessage.cancelEditNotification')}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.yes')}
                onClick={localConfirmCancelEditNotification}
              />
              <DefaultButton
                text={translator('global.no')}
                onClick={localDeclineCancelEditNotification}
              />
            </div>
          </div>
        );
      case globalMessageModes.downloadNotificationHasChanges:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator(
                  'components.globalMessage.downloadNotificationHasChangesHeader',
                )}
              </h2>
              <p>
                {translator(
                  'components.globalMessage.notificationHasChangesBody1',
                )}
              </p>
              <p>
                {translator(
                  'components.globalMessage.notificationHasChangesBody2',
                )}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.back')}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.sendNotificationHasChanges:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator(
                  'components.globalMessage.sendNotificationSuccessHeader',
                )}
              </h2>
              <p>
                {translator(
                  'components.globalMessage.notificationHasChangesBody1',
                )}
              </p>
              <p>
                {translator(
                  'components.globalMessage.notificationHasChangesBody2',
                )}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.back')}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.sendNotificationSuccessful:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator(
                  'components.globalMessage.sendNotificationSuccessHeader',
                )}
              </h2>
              <p>
                {translator(
                  'components.globalMessage.sendNotificationSuccessBody1',
                )}
              </p>
              <p>
                {translator(
                  'components.globalMessage.sendNotificationSuccessBody2',
                )}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.back')}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      case globalMessageModes.sendNotificationFailed:
        return (
          <div className="modal-outer">
            <div className="modal-outer-content">
              <h2 className="h2-style">
                {translator(
                  'components.globalMessage.sendNotificationFailedHeader',
                )}
              </h2>
              <p>
                {translator(
                  'components.globalMessage.sendNotificationFailedBody',
                )}
              </p>
            </div>
            <div className="global-message-modal-button-bar">
              <PrimaryButton
                text={translator('global.back')}
                onClick={dismiss}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="global-message">
      {globalMessageMode && (
        <Modal isOpen={showModal} onDismiss={dismiss} isBlocking={true}>
          {renderModalContent(globalMessageMode)}
        </Modal>
      )}
    </div>
  );
};

export { GlobalMessage };
